'use client';

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useRouter } from 'next/navigation';
import { Check, Globe } from 'lucide-react';
import { localeDefinitions, normalizeLocale, selectLocale, type Locale } from '@/i18n/locales';
import styles from './LanguageSwitcher.module.css';

interface LanguageSwitcherProps {
  currentLocale: string;
  label?: string;
  showName?: boolean;
  showLabel?: boolean;
  placement?: 'top' | 'bottom' | 'right';
  variant?: 'default' | 'toolbar' | 'plain' | 'rail' | 'sidebar';
}

export function LanguageSwitcher({ currentLocale, label = 'Language', showName = false, showLabel = false, placement = 'bottom', variant = 'default' }: LanguageSwitcherProps) {
  const locale = normalizeLocale(currentLocale);
  const router = useRouter();
  const root = useRef<HTMLDetailsElement>(null);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) root.current?.removeAttribute('open');
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);
  useEffect(() => { setPending(false); }, [currentLocale]);

  const choose = (nextLocale: Locale) => {
    const href = selectLocale(nextLocale);
    root.current?.removeAttribute('open');
    root.current?.querySelector('summary')?.focus();
    if (locale === nextLocale) return;
    setPending(true);
    // Static Pages Router pages need a document navigation to their locale-specific ISR output.
    if (location.pathname === '/' || location.pathname.startsWith('/guides')) {
      window.location.assign(href);
    } else {
      router.replace(href, { scroll: false });
      router.refresh();
    }
  };
  const navigate = (event: KeyboardEvent<HTMLDetailsElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      root.current?.removeAttribute('open');
      root.current?.querySelector('summary')?.focus();
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    root.current?.setAttribute('open', '');
    const items = Array.from(root.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
      : (index + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
    items[next]?.focus();
  };
  return (
    <details ref={root} className={styles.root} onKeyDown={navigate} aria-busy={pending}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) root.current?.removeAttribute('open'); }}>
      <summary className={`${styles.trigger} ${variant === 'toolbar' ? styles.toolbarTrigger : variant === 'plain' ? styles.plainTrigger : variant === 'rail' ? styles.railTrigger : variant === 'sidebar' ? styles.sidebarTrigger : ''}`} aria-label={variant === 'sidebar' || variant === 'rail' ? label : `${label}: ${localeDefinitions[locale].name}`} title={label}>
        <Globe size={18} aria-hidden="true" />
        {showLabel ? <span>{label}</span> : showName ? <span>{localeDefinitions[locale].name}</span> : null}
      </summary>
      <div className={`${styles.menu} ${placement === 'top' ? styles.menuTop : placement === 'right' ? styles.menuRight : ''}`} aria-label={label}>
        {Object.entries(localeDefinitions).map(([key, item]) => (
          <button key={key} type="button" lang={item.html} disabled={pending}
            className={styles.item} aria-pressed={key === locale} onClick={() => choose(key as Locale)}>
            <span className={styles.flag} aria-hidden="true">{item.flag}</span>
            <span>{item.name}</span>
            {key === locale && <Check size={16} className={styles.check} aria-hidden="true" />}
          </button>
        ))}
      </div>
    </details>
  );
}
